/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import {
  CheckOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  saveAdditionalItems,
  updateAdditionalItems,
  deleteAdditionalItems,
  getPatientMedicalHistory
} from 'services/soap/action';

const AdditionalItems = props => {
  const { classes, patientId, ptMedicalHistory, getPatientMedicalHistory } = props;

  const initialUserState = { id: 0, description: '' };

  const [values, setValues] = useState(initialUserState);
  const [editvalues, setEditValues] = useState(initialUserState);
  const [AI, setAI] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // Granular loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    // Removed length > 0 guard so an empty array also clears the list
    if (ptMedicalHistory?.additionalItems) {
      setAI(ptMedicalHistory.additionalItems);
    }
  }, [ptMedicalHistory]);

  const handleFieldChange = (field, value) => {
    setValues({ ...values, [field]: value });
  };

  const handleEditFieldChange = (field, value) => {
    setEditValues({ ...editvalues, [field]: value });
  };

  const handleSaveAdditionalItems = async () => {
    if (!values.description || isSaving) return;
    setIsSaving(true);
    try {
      const res = await props.saveAdditionalItems(patientId, values);
      if (res.status) {
        setValues(initialUserState);
        getPatientMedicalHistory(patientId);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateAdditionalItems = async idx => {
    if (!editvalues.description || isUpdating) return;
    setIsUpdating(true);
    try {
      const res = await props.updateAdditionalItems(patientId, editvalues);
      if (res.status) {
        // Immediately reflect the change in local state
        setAI(prev =>
          prev.map(item =>
            item.id === editvalues.id
              ? { ...item, description: editvalues.description }
              : item
          )
        );
        setIsEdit(false);
        getPatientMedicalHistory(patientId);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAdditionalItems = async id => {
    if (deletingId === id) return;
    setDeletingId(id);
    try {
      const res = await props.deleteAdditionalItems(patientId, id);
      if (res.status) {
        // Immediately remove the deleted record from local state
        setAI(prev => prev.filter(item => item.id !== id));
        setValues(initialUserState);
        getPatientMedicalHistory(patientId);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditAdditionalItems = (data, index) => {
    setIsEdit(true);
    if (data) {
      setEditValues({ id: data.id, description: data.description, editIndex: index });
    }
  };

  const clearAdditionalItems = () => {
    setIsEdit(false);
    setValues(initialUserState);
  };

  const spinnerStyle = { color: '#03A484', width: 18, height: 18 };
  const deleteSpinnerStyle = { color: '#DE5555', width: 18, height: 18 };

  return (
    <div className={classes.templateWrap}>
      <div className={classes.templateBody} style={{ borderTop: 'default' }}>
        <PerfectScrollbar>
          <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
            <Table className={classes.templateTable}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TextField
                      className={classes.textBox}
                      id="outlined-basic"
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={event => handleFieldChange('description', event.target.value)}
                      placeholder="Input description"
                      value={values.description}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell width="10%" align="center" style={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Save" placement="top">
                      <span>
                        <IconButton
                          disabled={isSaving || !values.description}
                          onClick={handleSaveAdditionalItems}
                          size="small"
                          style={{ color: '#03A484' }}>
                          {isSaving
                            ? <CircularProgress style={spinnerStyle} />
                            : <CheckOutlined />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Clear" placement="top">
                      <span>
                        <IconButton
                          disabled={isSaving}
                          onClick={clearAdditionalItems}
                          size="small">
                          <ClearOutlined style={{ color: '#DE5555' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {AI && AI.length > 0 && AI.map((item, idx) => {
                  const isDeleting = deletingId === item.id;
                  const isEditRow = isEdit && idx === editvalues.editIndex;

                  return (
                    <TableRow key={item.id ?? idx}>
                      {isEditRow ? (
                        <TableCell>
                          <TextField
                            className={classes.textBox}
                            id={`edit-outlined-${idx}`}
                            InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                            onChange={event =>
                              handleEditFieldChange('description', event.target.value)
                            }
                            placeholder="Input description"
                            value={editvalues.description}
                            variant="outlined"
                          />
                        </TableCell>
                      ) : (
                        <TableCell>{item.description}</TableCell>
                      )}

                      <TableCell align="center" width={72}>
                        {isEditRow ? (
                          <>
                            <Tooltip title="Save changes" placement="top">
                              <span>
                                <IconButton
                                  disabled={isUpdating || !editvalues.description}
                                  onClick={() => handleUpdateAdditionalItems(idx)}
                                  size="small"
                                  style={{ color: '#03A484' }}>
                                  {isUpdating
                                    ? <CircularProgress style={spinnerStyle} />
                                    : <CheckOutlined />}
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Cancel" placement="top">
                              <span>
                                <IconButton
                                  disabled={isUpdating}
                                  onClick={clearAdditionalItems}
                                  size="small">
                                  <ClearOutlined style={{ color: '#DE5555' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit" placement="top">
                              <span>
                                <IconButton
                                  disabled={isDeleting || !!deletingId}
                                  onClick={() => handleEditAdditionalItems(item, idx)}
                                  size="small">
                                  <Edit color="primary" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Delete" placement="top">
                              <span>
                                <IconButton
                                  disabled={isDeleting || !!deletingId}
                                  onClick={() => handleDeleteAdditionalItems(item.id)}
                                  size="small">
                                  {isDeleting
                                    ? <CircularProgress style={deleteSpinnerStyle} />
                                    : <DeleteOutline style={{ color: '#DE5555' }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

AdditionalItems.propTypes = { className: PropTypes.string };

const mapDispatchToProps = {
  saveAdditionalItems,
  updateAdditionalItems,
  deleteAdditionalItems,
  getPatientMedicalHistory
};

const mapStateToProps = state => ({
  ptMedicalHistory: state.soap && state.soap.ptMedicalHistory
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(AdditionalItems))
);